import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
} from 'firebase/auth';
import { auth, googleProvider, appleProvider, initFirebaseFromRuntimeConfig } from '../config/firebase';
import { authFlowFlags } from '../lib/authFlowFlags';
import { fetchRuntimeConfig } from '../lib/runtimeConfig';

/* -------------------------------------------------------------------------- */
/*  fetchWithRetries (unchanged)                                              */
/* -------------------------------------------------------------------------- */
export async function fetchWithRetries(
  url: string,
  opts: RequestInit = {},
  retries = 5,
  delayMs = 300
): Promise<Response | null> {
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, opts);
      if (res.ok) return res;

      if (res.status === 401) throw new Error('Unauthorized');

      if (res.status >= 500 && res.status < 600) {
        await new Promise(r => setTimeout(r, delayMs * (1 + i)));
        continue;
      }

      throw new Error(`Request failed: ${res.status}`);
    } catch (err) {
      if (i === retries - 1) return null;
      await new Promise(r => setTimeout(r, delayMs * (1 + i)));
    }
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */
export interface UserProfile {
  name: string;
  email: string;
  phoneNumber: string;
  surveyCompleted: boolean;
  surveyResponses?: Record<string, string>;
  plans?: {
    mind?: string;
    diet?: string;
    exercise?: string;
  };
}

export interface User {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  surveyCompleted: boolean;
  plans?: {
    mind?: string;
    diet?: string;
    exercise?: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, phoneNumber: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  logout: () => void;
  updateUserProfile: (profile: Partial<UserProfile>) => Promise<void>;
  completeSurvey: (responses: Record<string, string>) => Promise<{ smsStatus?: string } | void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* -------------------------------------------------------------------------- */
/*  AuthProvider                                                              */
/* -------------------------------------------------------------------------- */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /* ------------------------------------------------------------------------ */
  /*  Auto‑logout on inactivity (unchanged)                                   */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!user) return;

    const timeout = 15 * 60 * 1000;
    let timer = setTimeout(() => {
      signOut(auth).then(() => setUser(null));
    }, timeout);

    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        signOut(auth).then(() => setUser(null));
      }, timeout);
    };

    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(evt =>
      window.addEventListener(evt, reset, { passive: true })
    );

    return () => {
      clearTimeout(timer);
      ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(evt =>
        window.removeEventListener(evt, reset)
      );
    };
  }, [user]);

  /* ------------------------------------------------------------------------ */
  /*  onAuthStateChanged — STRICT MODE                                        */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | null = null;

    const setup = async () => {
      const cfg = await fetchRuntimeConfig();
      if (cfg?.firebase?.apiKey) {
        await initFirebaseFromRuntimeConfig(cfg);
      } else {
        setIsLoading(false);
        return;
      }

      // If cleanup already ran during the async work above, bail out
      if (!mounted) return;

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
        if (!mounted) return;

        if (!firebaseUser) {
          setUser(null);
          setIsLoading(false);
          return;
        }

        try {
          const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
          const url = `${API_BASE}/auth/me`;
          const idToken = await firebaseUser.getIdToken();

          /* -------------------------------------------------------------- */
          /*  Wait until signup upsert is confirmed before calling /auth/me */
          /* -------------------------------------------------------------- */
          if (authFlowFlags.signupInProgress) {
            await new Promise<void>((resolve) => {
              const interval = setInterval(() => {
                if (!authFlowFlags.signupInProgress) {
                  clearInterval(interval);
                  resolve();
                }
              }, 50);
              // Safety timeout: don't hang forever if the flag is never cleared
              setTimeout(() => { clearInterval(interval); resolve(); }, 30_000);
            });
          }

          if (!mounted) return;

          const meRes = await fetchWithRetries(url, {
            method: 'GET',
            headers: { Authorization: `Bearer ${idToken}` },
          });

          if (!mounted) return;

          if (meRes && meRes.ok) {
            const meJson = await meRes.json().catch(() => null);
            const backend = meJson?.data;

            setUser({
              id: firebaseUser.uid,
              name: backend?.name || firebaseUser.displayName || '',
              email: backend?.email || firebaseUser.email || '',
              phoneNumber: backend?.phoneNumber || '',
              surveyCompleted: backend?.surveyCompleted || false,
              plans: backend?.plans,
            });
          } else {
            setUser(null);
          }
        } catch (err) {
          console.error('AuthContext error:', err);
          setUser(null);
        } finally {
          if (mounted) setIsLoading(false);
        }
      });
    };

    setup();
    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /*  login / signup / social login (unchanged except no fallback)            */
  /* ------------------------------------------------------------------------ */
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (name: string, email: string, phoneNumber: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: name });

      try {
        const idToken = await userCredential.user.getIdToken();
        const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
        await fetch(`${API_BASE}/auth/upsert-social`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ name, email, phoneNumber }),
        });
      } catch (err) {
        console.warn('Backend upsert after signup failed:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      try {
        const idToken = await result.user.getIdToken();
        const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
        await fetch(`${API_BASE}/auth/upsert-social`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            name: result.user.displayName,
            email: result.user.email,
            phoneNumber: '',
          }),
        });
      } catch (err) {
        console.warn('Google upsert failed:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithApple = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, appleProvider);
      try {
        const idToken = await result.user.getIdToken();
        const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
        await fetch(`${API_BASE}/auth/upsert-social`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            name: result.user.displayName,
            email: result.user.email,
            phoneNumber: '',
          }),
        });
      } catch (err) {
        console.warn('Apple upsert failed:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const updateUserProfile = async (profile: Partial<UserProfile>) => {
    if (!auth.currentUser) return;

    try {
      const idToken = await auth.currentUser.getIdToken();
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      await fetch(`${API_BASE}/auth/upsert-social`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(profile),
      });
    } catch (err) {
      console.warn('Profile update failed:', err);
    }

    setUser(prev => (prev ? { ...prev, ...profile } as User : null));
  };

  const completeSurvey = async (responses: Record<string, string>) => {
    if (!auth.currentUser) return;

    try {
      const idToken = await auth.currentUser.getIdToken();
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const res = await fetch(`${API_BASE}/signup/survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ idToken, surveyId: 'frontend_v1', responses }),
      });

      if (!res.ok) throw new Error('Survey submission failed');

      const json = await res.json().catch(() => ({}));
      setUser(prev => (prev ? { ...prev, surveyCompleted: true } as User : null));
      return { smsStatus: json?.smsStatus };
    } catch (err) {
      console.error('Survey error:', err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        loginWithGoogle,
        loginWithApple,
        logout,
        updateUserProfile,
        completeSurvey,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/*  Hook                                                                       */
/* -------------------------------------------------------------------------- */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}